import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { DebtFormData } from '@/hooks/useDebts';

const debtSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido').max(100),
  creditor: z.string().max(100).optional(),
  initial_balance: z.coerce.number().positive('El saldo debe ser mayor a 0'),
  current_balance: z.coerce.number().min(0, 'El saldo no puede ser negativo'),
  interest_rate: z.coerce.number().min(0, 'La tasa no puede ser negativa').max(100),
  minimum_payment: z.coerce.number().positive('El pago mínimo debe ser mayor a 0'),
  due_day: z.coerce.number().min(1).max(31).optional(),
});

interface DebtFormProps {
  onSubmit: (data: DebtFormData) => void;
  isLoading?: boolean;
}

export function DebtForm({ onSubmit, isLoading }: DebtFormProps) {
  const [open, setOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<DebtFormData>({
    resolver: zodResolver(debtSchema),
    defaultValues: {
      interest_rate: 0,
    },
  });

  const initialBalance = watch('initial_balance');

  const handleFormSubmit = (data: DebtFormData) => {
    onSubmit(data);
    reset();
    setOpen(false);
  };

  const handleInitialBalanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    setValue('initial_balance', value);
    setValue('current_balance', value);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="h-4 w-4" />
          Agregar Deuda
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Agregar Nueva Deuda</DialogTitle>
          <DialogDescription>
            Ingresa los detalles de tu deuda para incluirla en el plan bola de nieve.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre de la deuda *</Label>
              <Input
                id="name"
                placeholder="Ej: Tarjeta Visa"
                {...register('name')}
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="creditor">Acreedor</Label>
              <Input
                id="creditor"
                placeholder="Ej: Banco Nacional"
                {...register('creditor')}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="initial_balance">Saldo inicial *</Label>
              <Input
                id="initial_balance"
                type="number"
                step="0.01"
                placeholder="0.00"
                onChange={handleInitialBalanceChange}
              />
              {errors.initial_balance && (
                <p className="text-sm text-destructive">{errors.initial_balance.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="current_balance">Saldo actual *</Label>
              <Input
                id="current_balance"
                type="number"
                step="0.01"
                placeholder="0.00"
                {...register('current_balance')}
              />
              {errors.current_balance && (
                <p className="text-sm text-destructive">{errors.current_balance.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="interest_rate">Tasa de interés anual (%)</Label>
              <Input
                id="interest_rate"
                type="number"
                step="0.01"
                placeholder="0.00"
                {...register('interest_rate')}
              />
              {errors.interest_rate && (
                <p className="text-sm text-destructive">{errors.interest_rate.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="minimum_payment">Pago mínimo mensual *</Label>
              <Input
                id="minimum_payment"
                type="number"
                step="0.01"
                placeholder="0.00"
                {...register('minimum_payment')}
              />
              {errors.minimum_payment && (
                <p className="text-sm text-destructive">{errors.minimum_payment.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="due_day">Día de pago (1-31)</Label>
            <Input
              id="due_day"
              type="number"
              min="1"
              max="31"
              placeholder="Ej: 15"
              {...register('due_day')}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Guardando...' : 'Agregar Deuda'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
